import { Event } from '@/types'
import { EventState } from '@/components/common'
import { SlUser } from 'react-icons/sl'
import capitalize from '@/utils/capitalize'
import { formatDate } from '@/utils/formatDate'
import { format, toZonedTime } from 'date-fns-tz'
import { includes } from 'lodash'
import { isBefore } from 'date-fns'
import { currencyFormat, numberFormat } from '@/utils/formatNumber'
import { StatsBar, StatItem } from '@/components/event-detail'

interface EventStatsProps {
  event: Event
}

export const EventStats = ({ event }: EventStatsProps) => {
  const items: StatItem[] = [
    {
      label: 'Status',
      value: event.participation_info?.is_registered ? (
        <span className="text-sm px-2 py-1 rounded-md bg-gray-100 text-gray-500">
          Registered
        </span>
      ) : (
        <EventState event={event} />
      ),
    },
  ]

  if (event.show_participants) {
    items.push({
      label: 'Participants',
      value: (
        <>
          <SlUser />
          <span className="font-semibold">
            {numberFormat(event.registration_count || 0)}
          </span>
        </>
      ),
    })
  }

  items.push({
    label: 'Prize Pool',
    value: (
      <div className="flex flex-col items-center gap-2 px-2 md:px-4 lg:px-6">
        <span className="font-semibold">
          {currencyFormat(event.prize_currency, event.prize_amount)}
        </span>
        <span className="text-xs">{event.prize_currency}</span>
      </div>
    ),
  })

  items.push({
    label: 'Format',
    value: capitalize(event.category),
  })

  if (event.category !== 'virtual') {
    items.push({
      label: 'Venue',
      value: (
        <div className="flex flex-col items-center gap-2 px-2 md:px-4 lg:px-6">
          <span className="font-semibold">{event.venue?.location}</span>
          <span className="text-sm text-gray-500 text-center">
            {event.venue?.city}, {event.venue?.country}
          </span>
        </div>
      ),
    })
  }

  if (event.state === 'upcoming') {
    items.push({
      label: 'Registration Starts on',
      value: (
        <div className="flex flex-col items-center gap-2 px-2 md:px-4 lg:px-6">
          <span className="font-semibold text-center">
            {formatDate(
              event.registration_start_date,
              'dd MMM, yyyy, h:mma',
              event.zoneinfo
            )}
          </span>
          <span className="text-xs">
            ({format(new Date(), 'zzz', { timeZone: event.zoneinfo })})
          </span>
        </div>
      ),
    })
  }

  if (
    includes(['ongoing', 'register_now'], event.state) &&
    event.submission_start_date &&
    isBefore(
      toZonedTime(new Date(), event.zoneinfo),
      toZonedTime(event.submission_start_date, event.zoneinfo)
    )
  ) {
    items.push({
      label: 'Submission Starts on',
      value: (
        <div className="flex flex-col items-center gap-2 px-2 md:px-4 lg:px-6">
          <span className="font-semibold text-center">
            {formatDate(
              event.submission_start_date,
              'dd MMM, yyyy, h:mma',
              event.zoneinfo
            )}
          </span>
          <span className="text-xs">
            ({format(new Date(), 'zzz', { timeZone: event.zoneinfo })})
          </span>
        </div>
      ),
    })
  }

  if (
    includes(['ongoing', 'register_now'], event.state) &&
    event.submission_end_date &&
    !isBefore(
      toZonedTime(new Date(), event.zoneinfo),
      toZonedTime(event.submission_start_date, event.zoneinfo)
    )
  ) {
    items.push({
      label: 'Submissions Deadline',
      value: (
        <div className="flex flex-col items-center gap-2 px-2 md:px-4 lg:px-6">
          <span className="font-semibold text-center">
            {formatDate(
              event.submission_end_date,
              'dd MMM, yyyy, h:mma',
              event.zoneinfo
            )}
          </span>
          <span className="text-xs">
            ({format(new Date(), 'zzz', { timeZone: event.zoneinfo })})
          </span>
        </div>
      ),
    })
  }

  return <StatsBar items={items} />
}
